const DAY_MS = 86_400_000

export function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
}

/**
 * The zone the reader is in, which the browser already knows. Nothing has to be
 * stored or asked for a page to name the day in front of somebody.
 */
function readerZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * The calendar day an instant falls on, somewhere.
 *
 * The locale is named rather than inherited so that the host's cannot decide the
 * digits: a numbering system other than Latin would render this unparseable
 * everywhere it is compared against a stored date. The parts are read by name
 * for the same reason — a locale that orders or separates them differently must
 * not change the answer.
 */
export function dateIn(at: Date, zone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at)
  const of = (type: string): string => parts.find((part) => part.type === type)?.value ?? ''
  return `${of('year')}-${of('month')}-${of('day')}`
}

/**
 * The day the reader is living, which is not the day Greenwich is.
 *
 * Four components each spelled this `new Date().toISOString().slice(0, 10)`,
 * the UTC date: after about 16:00 in Los Angeles it names tomorrow. The cost was
 * not cosmetic — every date a household types is pre-filled from here, so a
 * holding entered on Tuesday evening was filed on Wednesday, and an age counted
 * from it was a day out.
 */
export function today(at: Date = new Date(), zone: string = readerZone()): string {
  return dateIn(at, zone)
}

/** Negative when `to` is the earlier of the two. */
export function daysBetween(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`)
  const end = Date.parse(`${to}T00:00:00Z`)
  if (Number.isNaN(start) || Number.isNaN(end)) return 0
  return Math.round((end - start) / DAY_MS)
}

export function describeAge(days: number): string {
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 45) return `${days} days ago`
  const months = Math.round(days / 30)
  if (months < 24) return `${months} months ago`
  return `${(days / 365).toFixed(1)} years ago`
}

export function describeDuration(days: number): string {
  if (days <= 0) return 'less than a day'
  if (days === 1) return '1 day'
  if (days < 45) return `${days} days`
  const months = Math.round(days / 30)
  return months < 24 ? `${months} months` : `${(days / 365).toFixed(1)} years`
}
